"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Users, Cpu, Code as Code2, Wrench, Check, Globe, Sparkles, ArrowRight, ShieldCheck, Zap } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type Lang = "vi" | "en";
interface ModelContent {
  badge: string;
  title: string;
  subtitle: string;
  tagline: string;
  roles: {
    title: string;
    description: string;
    iconName: "wrench" | "code";
  }[];
  highlights: string[];
  instructorNote: string;
}
const contentData: Record<Lang, {
  sectionTitle: string;
  sectionSubtitle: string;
  specRailLabel: string;
  ctaText: string;
  models: [ModelContent, ModelContent];
}> = {
  vi: {
    sectionTitle: "MÔ HÌNH HỌC TẬP THEO NHÓM NHỎ",
    sectionSubtitle: "Tối ưu tương tác giữa giảng viên và học viên với tỉ lệ thiết bị tiêu chuẩn academy.",
    specRailLabel: "MÔ HÌNH TƯƠNG TÁC THIẾT BỊ ROBOTO",
    ctaText: "Đăng ký trải nghiệm thực tế",
    models: [{
      badge: "Tỉ lệ lý tưởng",
      title: "2 học sinh / bộ kit (Lý tưởng)",
      subtitle: "Mô hình chuẩn đào tạo chuyên sâu",
      tagline: "Đảm bảo 100% thời lượng thực hành trực tiếp trên thiết bị cho mỗi học sinh.",
      roles: [{
        title: "Học sinh 1: Lắp ráp & Cơ khí",
        description: "Đảm nhận thiết kế khung vỏ, căn chỉnh động cơ, cảm biến và căn chỉnh phần cứng.",
        iconName: "wrench"
      }, {
        title: "Học sinh 2: Lập trình & Logic",
        description: "Xây dựng khối lệnh logic, nạp chương trình và kiểm thử thuật toán điều khiển.",
        iconName: "code"
      }],
      highlights: ["Giảng viên theo sát và tương tác trực tiếp từng nhóm", "Phân công vai trò xoay vòng sau mỗi buổi học", "Tối đa hóa tư duy giải quyết vấn đề độc lập"],
      instructorNote: "Mức độ chú ý của giáo viên đạt tối đa cho từng cá nhân."
    }, {
      badge: "Tối ưu chi phí",
      title: "3 học sinh / bộ kit (Tối ưu chi phí)",
      subtitle: "Cân bằng chi phí & kỹ năng làm việc nhóm",
      tagline: "Phù hợp cho lớp học phong trào và phát triển tinh thần phối hợp đồng đội.",
      roles: [{
        title: "Phân chia công việc linh hoạt",
        description: "1 học sinh lắp ráp, 1 lập trình viên chính và 1 chuyên viên kiểm thử & báo cáo.",
        iconName: "wrench"
      }, {
        title: "Xoay vòng nhiệm vụ",
        description: "Luân chuyển vị trí liên tục để mọi học sinh đều thành thạo cả cơ khí lẫn thuật toán.",
        iconName: "code"
      }],
      highlights: ["Tiết kiệm chi phí đầu tư học phí hàng tháng", "Rèn luyện kỹ năng giao tiếp và làm việc nhóm thực tế", "Đảm bảo tiến độ hoàn thành dự án chung"],
      instructorNote: "Đảm bảo tính tối ưu nguồn lực và tinh thần đồng đội."
    }]
  },
  en: {
    sectionTitle: "Small-Group Learning Model",
    sectionSubtitle: "Maximizing instructor interaction and hands-on kit practice with academy standards.",
    specRailLabel: "ROBOTO EQUIPMENT-TO-STUDENT RATIO",
    ctaText: "Book a Free Trial Class",
    models: [{
      badge: "Ideal Ratio",
      title: "2 students / kit (Ideal ratio)",
      subtitle: "Gold Standard for Deep Technical Mastery",
      tagline: "Guarantees 100% active hands-on time per equipment unit for every student.",
      roles: [{
        title: "Student 1: Mechanical & Hardware Alignment",
        description: "Leads structural assembly, motor calibration, and sensor wiring alignment.",
        iconName: "wrench"
      }, {
        title: "Student 2: Block Logic & Program Uploads",
        description: "Constructs algorithmic logic, uploads firmware, and tests robotics behaviors.",
        iconName: "code"
      }],
      highlights: ["High focused instructor attention per individual student", "Role rotation in every session for balanced skillset", "Maximizes independent problem-solving capabilities"],
      instructorNote: "Enables highly focused instructor guidance per student."
    }, {
      badge: "Cost-Optimized",
      title: "3 students / kit (Cost-optimized)",
      subtitle: "Balanced Collaboration & Resource Optimization",
      tagline: "Designed for efficient group dynamics, resource optimization, and team bonding.",
      roles: [{
        title: "Collaborative Role Split",
        description: "1 lead builder, 1 lead coder, and 1 QA tester & presentation specialist.",
        iconName: "wrench"
      }, {
        title: "Structured Rotation",
        description: "Rotates responsibilities systematically so every student masters hardware & code.",
        iconName: "code"
      }],
      highlights: ["Optimizes course fee accessibility", "Develops real-world teamwork and communication skills", "Maintains efficient project completion pacing"],
      instructorNote: "Optimized for team dynamics and resource accessibility."
    }]
  }
};
export default function SmallGroupLearningModel() {
  const [lang, setLang] = useState<Lang>("vi");
  const t = contentData[lang];
  return <section id="smallgrouplearningmodel" data-nav="dark" className="relative overflow-hidden bg-slate-950 text-slate-100 py-20 lg:py-28">
      {/* Blueprint Grid & Decorative Kit Overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-20 bg-[linear-gradient(to_right,#00bcd415_1px,transparent_1px),linear-gradient(to_bottom,#00bcd415_1px,transparent_1px)] bg-[size:32px_32px]" aria-hidden="true" />
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-secondary/15 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-primary/20 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Spec Rail Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pb-6 mb-12 border-b border-lime-400/40">
          <div className="flex items-center gap-3">
            <span className="h-2.5 w-2.5 rounded-full bg-lime-400 animate-pulse" />
            <span className="text-xs font-mono tracking-widest text-lime-400 uppercase font-semibold">
              {t.specRailLabel}
            </span>
          </div>

          {/* Language Toggle Button (roboto_lang pattern) */}
          <div className="flex items-center gap-2 bg-slate-900/80 p-1 rounded-lg border border-slate-800">
            <Globe className="size-4 text-slate-400 ml-2" />
            <Button variant={lang === "vi" ? "secondary" : "ghost"} size="sm" onClick={() => setLang("vi")} className={`text-xs h-7 px-3 font-medium transition-all ${lang === "vi" ? "bg-secondary text-secondary-foreground font-semibold" : "text-slate-300 hover:text-white hover:bg-slate-800"}`}>
              Tiếng Việt
            </Button>
            <Button variant={lang === "en" ? "secondary" : "ghost"} size="sm" onClick={() => setLang("en")} className={`text-xs h-7 px-3 font-medium transition-all ${lang === "en" ? "bg-secondary text-secondary-foreground font-semibold" : "text-slate-300 hover:text-white hover:bg-slate-800"}`}>
              English
            </Button>
          </div>
        </div>

        {/* Headline Section */}
        <div className="max-w-3xl mb-12">
          <Badge variant="outline" className="mb-4 border-lime-400/40 text-lime-300 bg-lime-950/30 font-mono text-xs uppercase tracking-wide">
            ROBOTO Academy Standard
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
            {t.sectionTitle}
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed">
            {t.sectionSubtitle}
          </p>
        </div>

        {/* Featured Classroom Setting Photograph */}
        <div className="mb-14 rounded-2xl overflow-hidden border border-slate-800 bg-slate-900/80 shadow-2xl relative group">
          <div className="relative aspect-[21/9] min-h-[260px] sm:min-h-[340px] w-full overflow-hidden">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_small_group_classroom_lab.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_small_group_classroom_lab.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_small_group_classroom_lab.png-wvc-sizes" alt="ROBOTO small-group classroom setting illustrating student-per-kit learning models" loading="lazy" className="w-full h-full object-cover object-center transform group-hover:scale-105 transition-transform duration-700 ease-out" />
            {/* Dark & Deep-Blue Technical Gradients */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-transparent to-slate-950/60" />

            {/* Blueprint Corner Badges */}
            <div className="absolute top-4 left-4 sm:top-6 sm:left-6 flex flex-wrap items-center gap-2 z-10">
              <Badge variant="outline" className="border-cyan-400/50 bg-slate-950/80 text-cyan-300 font-mono text-xs backdrop-blur-md">
                LAB SPECIFICATION
              </Badge>
              <Badge variant="outline" className="border-lime-400/50 bg-slate-950/80 text-lime-300 font-mono text-xs backdrop-blur-md">
                2-STUDENT vs 3-STUDENT KIT MODEL
              </Badge>
            </div>

            {/* Tech Caption Overlay */}
            <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 flex flex-col sm:flex-row sm:items-end justify-between gap-3 z-10">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="size-2 rounded-full bg-lime-400 animate-pulse" />
                  <span className="text-xs font-mono tracking-wider text-lime-300 uppercase font-semibold">
                    REAL ACADEMY CLASSROOM ENVIRONMENT
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-slate-200 font-mono bg-slate-950/70 p-3 rounded-lg border border-slate-800/80 backdrop-blur-sm">
                  {lang === "vi" ? "Hình ảnh thực tế phòng học ROBOTO: Bố trí không gian tiêu chuẩn giúp tối ưu hóa sự tập trung, tương tác giữa giảng viên và từng nhóm học viên." : "Actual ROBOTO lab setup: Structured environment maximizing instructor attention and hands-on kit access for both 2-student and 3-student team models."}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Model Cards Comparison Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {t.models.map((model, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: idx * 0.12
        }} whileHover={{
          y: -4
        }} data-index={idx}>
              <Card className={`h-full flex flex-col justify-between bg-slate-900/90 border rounded-2xl overflow-hidden shadow-xl transition-colors ${idx === 0 ? "border-lime-400/50 shadow-lime-950/20" : "border-slate-800 hover:border-slate-700"}`}>
                <div>
                  {/* Model Classroom Photo Header */}
                  <div className="relative aspect-[16/9] w-full overflow-hidden border-b border-slate-800 bg-slate-950 group/photo">
                    <img src={idx === 0 ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_lab_2_students_kit.png" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_lab_3_students_kit.png"} data-wvc-srcset={idx === 0 ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_lab_2_students_kit.png-wvc-srcset" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_lab_3_students_kit.png-wvc-srcset"} data-wvc-sizes={idx === 0 ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_lab_2_students_kit.png-wvc-sizes" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_lab_3_students_kit.png-wvc-sizes"} alt={idx === 0 ? "Two students collaborating on a robotics kit" : "Three students collaborating in team roles with a robotics kit"} loading="lazy" className="w-full h-full object-cover transform group-hover/photo:scale-105 transition-transform duration-500 ease-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
                  </div>

                  {/* Top Header Banner inside Card */}
                  <div className={`px-6 py-4 border-b flex items-center justify-between ${idx === 0 ? "bg-lime-950/40 border-lime-400/30 text-lime-300" : "bg-slate-800/50 border-slate-800 text-slate-300"}`}>
                    <span className="text-xs font-mono uppercase tracking-wider font-semibold flex items-center gap-2">
                      {idx === 0 ? <Sparkles className="size-4 text-lime-400" /> : <Zap className="size-4 text-amber-400" />}
                      Model {idx + 1}
                    </span>
                    <Badge variant={idx === 0 ? "secondary" : "outline"} className={idx === 0 ? "bg-lime-400 text-slate-950 font-bold" : "border-slate-700 text-slate-300"}>
                      {model.badge}
                    </Badge>
                  </div>

                  <CardHeader className="pt-6 pb-2 px-6">
                    <CardTitle className="text-2xl font-bold text-white mb-1">
                      {model.title}
                    </CardTitle>
                    <p className="text-sm font-medium text-slate-400">
                      {model.subtitle}
                    </p>
                  </CardHeader>

                  <CardContent className="px-6 py-4 space-y-6">
                    {/* Tagline */}
                    <div className="p-3.5 rounded-lg bg-slate-950/60 border border-slate-800/80 text-sm text-slate-200">
                      {model.tagline}
                    </div>

                    {/* Distinct Roles Split */}
                    <div className="space-y-3">
                      <p className="text-xs font-mono uppercase tracking-wider text-slate-400 font-semibold">
                        {lang === "vi" ? "Phân công vai trò:" : "Role Division:"}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {model.roles.map((role, rIdx) => <div key={rIdx} data-index={rIdx} className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800 flex flex-col justify-between">
                            <div className="flex items-center gap-2 mb-2">
                              {role.iconName === "wrench" ? <Wrench className="size-4 text-lime-400 shrink-0" /> : <Code2 className="size-4 text-cyan-400 shrink-0" />}
                              <h4 className="text-xs font-bold text-slate-100">
                                {role.title}
                              </h4>
                            </div>
                            <p className="text-xs text-slate-400 leading-relaxed">
                              {role.description}
                            </p>
                          </div>)}
                      </div>
                    </div>

                    {/* Bullet Key Highlights */}
                    <div className="space-y-2 pt-2">
                      <p className="text-xs font-mono uppercase tracking-wider text-slate-400 font-semibold">
                        {lang === "vi" ? "Đặc điểm nổi bật:" : "Key Advantages:"}
                      </p>
                      <ul className="space-y-2">
                        {model.highlights.map((item, hIdx) => <li key={hIdx} data-index={hIdx} className="flex items-start gap-2.5 text-sm text-slate-300">
                            <Check className="size-4 text-lime-400 shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </div>

                {/* Footer Note */}
                <div className="px-6 py-4 bg-slate-950/80 border-t border-slate-800/80 flex items-center gap-3">
                  <ShieldCheck className="size-5 text-lime-400 shrink-0" />
                  <p className="text-xs text-slate-300 italic">
                    {model.instructorNote}
                  </p>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom CTA Block */}
        <div className="mt-16 p-8 rounded-2xl bg-slate-900/60 border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-slate-800 text-lime-400">
              <Users className="size-8" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">
                {lang === "vi" ? "Bạn muốn tìm hiểu mô hình phù hợp cho con?" : "Not sure which model suits your child best?"}
              </h3>
              <p className="text-sm text-slate-400">
                {lang === "vi" ? "Tham gia buổi học thử miễn phí để đánh giá sự tập trung và thích nghi của bé." : "Join a free trial session to observe your child's hands-on teamwork style."}
              </p>
            </div>
          </div>
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 shadow-md shrink-0">
            <Link to="/FreeTrialContactPage" className="inline-flex items-center gap-2">
              <span>{t.ctaText}</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}